// platform-info.service.ts
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Platform as CdkPlatform } from '@angular/cdk/platform';
import { Platform as IonicPlatform } from '@ionic/angular';

export interface AppPlatformInfo {
    /** true se siamo in browser (no SSR) */
    isBrowser: boolean;
    /** Piattaforme viste da Ionic (es: ['ios','mobile','capacitor']) */
    ionicPlatforms: string[];
    /** Browser/engine dal CDK (edge, blink, webkit, firefox, safari, trident) */
    engine: 'edge' | 'blink' | 'webkit' | 'firefox' | 'safari' | 'trident' | 'unknown';
    /** OS dedotto: 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'unknown' */
    os: 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'unknown';
    /** Form factor: 'mobile' | 'tablet' | 'desktop' | 'unknown' */
    formFactor: 'mobile' | 'tablet' | 'desktop' | 'unknown';
    /** true se app ibrida (Capacitor/Hybrid) secondo Ionic */
    isHybrid: boolean;
    /** true se PWA secondo Ionic */
    isPwa: boolean;
    /** Token finale pronto all’uso (uguale a `PlatformInfoService.token`) */
    token: string;
}

@Injectable({ providedIn: 'root' })
export class PlatformInfoService {
    constructor(
        @Inject(PLATFORM_ID) private readonly platformId: Object,
        private readonly cdk: CdkPlatform,
        private readonly ionic: IonicPlatform,
    ) { }

    /** Token sintetico sempre disponibile */
    get token(): string {
        return this.buildToken(this.getInfo());
    }

    /** Dettaglio completo se vuoi usarlo altrove */
    getInfo(): AppPlatformInfo {
        const isBrowser = isPlatformBrowser(this.platformId);

        if (!isBrowser) {
            const info: AppPlatformInfo = {
                isBrowser,
                ionicPlatforms: [],
                engine: 'unknown',
                os: 'unknown',
                formFactor: 'unknown',
                isHybrid: false,
                isPwa: false,
                token: 'server',
            };
            return info;
        }

        // ---- Ionic platforms ----
        const ionicPlatforms = this.ionic.platforms(); // es: ['ios','mobile','capacitor']
        const isHybrid = this.ionic.is('hybrid') || this.ionic.is('capacitor') || this.ionic.is('cordova');
        const isPwa = this.ionic.is('pwa');

        // ---- Engine/Browser dal CDK ----
        const engine: AppPlatformInfo['engine'] =
            this.cdk.EDGE ? 'edge' :
                this.cdk.FIREFOX ? 'firefox' :
                    this.cdk.SAFARI ? 'safari' :
                        this.cdk.WEBKIT ? 'webkit' :
                            this.cdk.BLINK ? 'blink' :
                                this.cdk.TRIDENT ? 'trident' :
                                    'unknown';

        // ---- OS dedotto ----
        const os: AppPlatformInfo['os'] =
            this.cdk.ANDROID ? 'android' :
                this.cdk.IOS ? 'ios' :
                    guessDesktopOs() ?? 'unknown';

        // ---- Form factor ----
        const formFactor: AppPlatformInfo['formFactor'] =
            this.ionic.is('ipad') ? 'tablet' :
                this.ionic.is('tablet') ? 'tablet' :
                    this.ionic.is('mobile') ? 'mobile' :
                        this.ionic.is('desktop') ? 'desktop' :
                            // fallback semplice su viewport
                            (typeof window !== 'undefined' && window.innerWidth >= 992) ? 'desktop' :
                                (typeof window !== 'undefined' && window.innerWidth >= 600) ? 'tablet' :
                                    'mobile';

        const base: AppPlatformInfo = {
            isBrowser,
            ionicPlatforms,
            engine,
            os,
            formFactor,
            isHybrid,
            isPwa,
            token: 'unknown',
        };

        base.token = this.buildToken(base);
        return base;

        // --- helpers ---
        function guessDesktopOs(): AppPlatformInfo['os'] | null {
            const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '').toLowerCase();
            if (!ua) return null;
            if (ua.includes('mac os') || ua.includes('macintosh')) return 'macos';
            if (ua.includes('win')) return 'windows';
            if (ua.includes('linux')) return 'linux';
            return null;
        }
    }

    /** Crea un token leggibile e stabile: es. "hybrid:android:mobile:blink" o "web:ios:tablet:safari" */
    private buildToken(info: AppPlatformInfo): string {
        if (!info.isBrowser) return 'server';

        const runtime = info.isHybrid ? 'hybrid'
            : info.isPwa ? 'pwa'
                : 'web';

        const parts = [
            runtime,
            info.os,
            info.formFactor,
            info.engine,
        ];

        // se Ionic ci dà una piattaforma primaria più specifica (ios/android/mobileweb/desktop), prefissa
        const primaryIonic = info.ionicPlatforms.find(p =>
            ['ios', 'android', 'mobileweb', 'desktop', 'electron', 'capacitor', 'cordova'].includes(p)
        );
        if (primaryIonic && !['ios', 'android'].includes(primaryIonic)) {
            // Evita duplicare os se già ios/android
            parts.unshift(primaryIonic);
        }

        return parts.filter(Boolean).join(':');
    }
}



/** Gestione della detection per ionic che non supporta DI */
export type AppPlatformShape = Pick<AppPlatformInfo, 'isBrowser' | 'os' | 'formFactor' | 'token'>;
/**
 * Helper “pure” ricavato dalla tua logica, utilizzabile fuori dal DI.
 * Non crea/usa istanze del servizio; accetta una Window e calcola info base.
 */
export function computePlatformFromWindow(win: Window): AppPlatformShape {
    const ua = win.navigator?.userAgent?.toLowerCase?.() ?? '';
    const isBrowser = true;

    // --- OS (semplificato ma coerente con il tuo guess) ---
    let os: AppPlatformInfo['os'] = 'unknown';
    if (/android/.test(ua)) os = 'android';
    else if (/iphone|ipad|ipod|ios/.test(ua)) os = 'ios';
    else if (/mac os|macintosh/.test(ua)) os = 'macos';
    else if (/win/.test(ua)) os = 'windows';
    else if (/linux/.test(ua)) os = 'linux';

    // --- Form factor: stessi breakpoint del tuo servizio (>=992 desktop, >=600 tablet) ---
    const w = win.innerWidth || 0;
    let formFactor: AppPlatformInfo['formFactor'] = 'mobile';
    if (w >= 992) formFactor = 'desktop';
    else if (w >= 600) formFactor = 'tablet';

    // --- Token sintetico compatibile con il tuo schema base ---
    const runtime = (win.matchMedia?.('(display-mode: standalone)')?.matches ?? false) ? 'pwa' : 'web';
    const engine: AppPlatformInfo['engine'] =
        /edg\//.test(ua) ? 'edge' :
            /firefox/.test(ua) ? 'firefox' :
                /safari/.test(ua) && !/chrome|crios|edg\//.test(ua) ? 'safari' :
                    /webkit/.test(ua) && !/chrome|crios|edg\//.test(ua) ? 'webkit' :
                        /chrome|crios|edg\//.test(ua) ? 'blink' : 'unknown';

    const token = [runtime, os, formFactor, engine].filter(Boolean).join(':');

    return { isBrowser, os, formFactor, token };
}